import React, { useState, useEffect } from 'react';
import { 
  FileCode2, 
  Plus, 
  Save, 
  Check, 
  DollarSign,
  Archive,
  Undo2,
  ArrowRight,
  Info,
  Download,
  Copy,
  SlidersHorizontal,
  Layers,
  FileCode,
  Sparkles,
  ExternalLink,
  X,
  ShieldCheck,
  Search,
  Filter
} from 'lucide-react';
import { Agent } from '../types';
import { api, type ProfileVersionRecord } from '../services/api';
import { AGENT_DIRECTIVE_PACKS, ControlPackDefinition } from '../data/agentDirectivePacks';

interface ProfilesProps {
  setActiveTab: (tab: string) => void;
  readOnly?: boolean;
  demoMode?: boolean;
  role?: 'owner' | 'admin' | 'operator' | 'viewer';
}

export const SafetyProfiles: React.FC<ProfilesProps> = ({ setActiveTab, readOnly = false, demoMode = false, role = 'admin' }) => {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [archivedAgents, setArchivedAgents] = useState<Agent[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const [savedSuccess, setSavedSuccess] = useState<boolean>(false);
  const [showExporterModal, setShowExporterModal] = useState<boolean>(false);
  const [exporterFormat, setExporterFormat] = useState<'directives_json' | 'claude_md' | 'cursorrules'>('directives_json');
  const [copiedExport, setCopiedExport] = useState<boolean>(false);

  // Control Pack selector state
  const [packCategoryFilter, setPackCategoryFilter] = useState<string>('ALL');
  const [packSearch, setPackSearch] = useState<string>('');
  const [activeAppliedPack, setActiveAppliedPack] = useState<ControlPackDefinition | null>(null);

  // Edit form state
  const [editName, setEditName] = useState<string>('');
  const [editOwner, setEditOwner] = useState<string>('');
  const [editPurpose, setEditPurpose] = useState<string>('');
  const [editVersion, setEditVersion] = useState<string>('v1.0.0');
  const [editStatus, setEditStatus] = useState<'protected' | 'monitoring' | 'at_risk' | 'quarantined'>('protected');
  const [editRiskTier, setEditRiskTier] = useState<string>('medium');
  const [editBlastRadius, setEditBlastRadius] = useState<'low' | 'medium' | 'high' | 'critical'>('medium');
  const [editLimit, setEditLimit] = useState<number>(5000);
  const [allowedActionsText, setAllowedActionsText] = useState<string>('');
  const [restrictedActionsText, setRestrictedActionsText] = useState<string>('');
  const [controlsText, setControlsText] = useState<string>('');
  const [changeReason, setChangeReason] = useState<string>('');
  const [historyRevision, setHistoryRevision] = useState(0);
  const [history, setHistory] = useState<{
    agentId: string | null;
    entries: ProfileVersionRecord[];
    loading: boolean;
    error: string | null;
  }>({ agentId: null, entries: [], loading: false, error: null });
  const selectedId = selectedAgent?.id ?? null;
  const hasStoredProfile = agents.some((agent) => agent.id === selectedId);
  const profileHistory = history.agentId === selectedId ? history.entries : [];
  const historyLoading = hasStoredProfile && (history.agentId !== selectedId || history.loading);
  const historyError = history.agentId === selectedId ? history.error : null;

  useEffect(() => {
    let current = true;
    setHistory({ agentId: selectedId, entries: [], loading: hasStoredProfile, error: null });
    if (selectedId && hasStoredProfile) {
      api.getProfileHistory(selectedId)
        .then((entries) => {
          if (current) setHistory({ agentId: selectedId, entries, loading: false, error: null });
        })
        .catch((err) => {
          if (current) setHistory({
            agentId: selectedId, entries: [], loading: false,
            error: err instanceof Error ? err.message : 'Profile history is unavailable.'
          });
        });
    }
    return () => { current = false; };
  }, [selectedId, hasStoredProfile, historyRevision]);

  const loadAgents = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getAgents({ includeArchived: true });
      const activeAgents = data.filter((agent) => !agent.archived_at);
      const archived = data.filter((agent) => Boolean(agent.archived_at));
      setAgents(activeAgents);
      setArchivedAgents(archived);
      const refreshedSelection = selectedAgent ? activeAgents.find((agent) => agent.id === selectedAgent.id) : activeAgents[0];
      if (refreshedSelection) {
        setSelectedAgent(refreshedSelection);
        if (!selectedAgent) {
          selectAgentForEdit(refreshedSelection);
        } else {
          setHistoryRevision((revision) => revision + 1);
        }
      }
    } catch (err) {
      console.error('Error fetching agents:', err);
      setError(err instanceof Error ? err.message : 'Agent profiles are unavailable.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAgents();
  }, []);

  const selectAgentForEdit = (agent: Agent) => {
    setSelectedAgent(agent);
    setEditName(agent.name);
    setEditOwner(agent.owner);
    setEditPurpose(agent.purpose);
    setEditVersion(agent.version);
    setEditStatus(agent.status);
    setEditBlastRadius(agent.blast_radius || 'medium');
    setEditLimit(agent.max_transaction_limit || 0);
    setAllowedActionsText((agent.allowed_actions || []).join('\n'));
    setRestrictedActionsText((agent.restricted_actions || []).join('\n'));
    setControlsText((agent.required_controls || []).join('\n'));
    setChangeReason('');
    setActiveAppliedPack(null);
    setSavedSuccess(false);
    setHistoryRevision((revision) => revision + 1);
  };

  const handleCreateNew = () => {
    if (readOnly) return;
    const newAgent: Agent = {
      id: `agent-custom-${Date.now()}`,
      name: 'New Autonomous Agent',
      owner: 'Engineering Team',
      purpose: 'Autonomous workflow agent requiring Zero-Trust safety boundaries.',
      version: 'v1.0.0',
      status: 'monitoring',
      risk_score: 25,
      blast_radius: 'medium',
      allowed_actions: ['read_data', 'get_status'],
      restricted_actions: ['drop_table', 'delete_records'],
      required_controls: ['RULE_SPEND_VELOCITY_CAP'],
      max_transaction_limit: 1000,
      risk_categories: ['Operational'],
      monitoring_requirements: ['Log all edge executions to D1 and R2']
    };
    setSelectedAgent(newAgent);
    selectAgentForEdit(newAgent);
  };

  const handleSave = async () => {
    if (!selectedAgent || readOnly) return;
    setSaving(true);
    setError(null);
    try {
      const allowed = allowedActionsText.split('\n').map((s) => s.trim()).filter(Boolean);
      const restricted = restrictedActionsText.split('\n').map((s) => s.trim()).filter(Boolean);
      const controls = controlsText.split('\n').map((s) => s.trim()).filter(Boolean);

      const updatedAgent: Agent = {
        ...selectedAgent,
        name: editName,
        owner: editOwner,
        purpose: editPurpose,
        version: editVersion,
        status: editStatus,
        blast_radius: editBlastRadius,
        max_transaction_limit: Number(editLimit),
        allowed_actions: allowed,
        restricted_actions: restricted,
        required_controls: controls,
        change_reason: changeReason.trim() || 'Safety profile updated via governance console.'
      };

      await api.saveAgent(updatedAgent);
      await loadAgents();
      selectAgentForEdit(updatedAgent);
      setSavedSuccess(true);
      setTimeout(() => setSavedSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving agent:', err);
      setError(err instanceof Error ? err.message : 'Unable to save profile.');
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async () => {
    if (!selectedAgent || !agents.some((agent) => agent.id === selectedAgent.id)) return;
    if (!window.confirm(`Archive ${selectedAgent.name}? It will leave the active fleet but remain in history.`)) return;
    setSaving(true);
    setError(null);
    try {
      await api.archiveAgent(selectedAgent.id);
      setSelectedAgent(null);
      await loadAgents();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to archive the profile.');
    } finally {
      setSaving(false);
    }
  };

  const handleRestore = async (agent: Agent) => {
    setSaving(true);
    setError(null);
    try {
      await api.restoreAgent(agent.id);
      await loadAgents();
      selectAgentForEdit({ ...agent, archived_at: null });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to restore the profile.');
    } finally {
      setSaving(false);
    }
  };

  const handleRollback = async (entry: ProfileVersionRecord) => {
    if (!selectedAgent || readOnly || historyLoading || historyError ||
      history.agentId !== selectedAgent.id || !profileHistory.some((item) => item.id === entry.id)) return;
    if (!window.confirm(`Restore ${selectedAgent.name} to version ${entry.version}? The current profile will be preserved in history as a new rollback record.`)) return;
    setSaving(true);
    setError(null);
    try {
      const result = await api.rollbackAgent(selectedAgent.id, entry.id, `Restored from profile history version ${entry.version}.`);
      await loadAgents();
      selectAgentForEdit(result.agent);
      setSavedSuccess(true);
      window.setTimeout(() => setSavedSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to roll back the profile.');
    } finally {
      setSaving(false);
    }
  };

  const applyControlPack = (pack: ControlPackDefinition) => {
    setActiveAppliedPack(pack);
    const isFinancial = pack.categories.includes('FINANCIAL') || pack.pack_id.startsWith('AUT');
    const isDevOps = pack.categories.includes('INFRASTRUCTURE') || pack.pack_id.startsWith('REL');
    const limit = isFinancial ? 5000 : isDevOps ? 0 : 1000;
    
    setEditLimit(limit);
    setEditPurpose(pack.summary);
    
    const reqStatements = pack.requirements.map(r => r.id + ': ' + r.statement).join('\n');
    setControlsText(reqStatements);
    
    if (pack.pack_id.startsWith('AI')) {
      setAllowedActionsText('read_content\nextract_entities\nquery_knowledgebase');
      setRestrictedActionsText('execute_raw_script\noverride_system_prompt\nexfiltrate_memory');
    } else if (pack.pack_id.startsWith('AUT')) {
      setAllowedActionsText('read_invoice\nextract_line_items\nverify_tax_id\nget_payment_status');
      setRestrictedActionsText('disburse_unapproved_wire\nmodify_payout_routing\nbypass_spend_cap');
    } else if (pack.pack_id.startsWith('DAT')) {
      setAllowedActionsText('read_sanitized_ticket\npost_ticket_reply\nmask_customer_pii');
      setRestrictedActionsText('export_all_contacts_csv\nread_unmasked_ssn\nexfiltrate_to_webhook');
    } else {
      setAllowedActionsText('read_metrics\nget_cluster_status\ndescribe_services');
      setRestrictedActionsText('delete_database\nterminate_production_cluster\nforce_push');
    }

    setChangeReason(`Applied ${pack.pack_id}: ${pack.title} (AgentDirective)`);
  };

  // Filtered 29 Control Packs
  const filteredPacks = AGENT_DIRECTIVE_PACKS.filter((p) => {
    const matchesCategory = 
      packCategoryFilter === 'ALL' || 
      p.pack_id.startsWith(packCategoryFilter);
    const matchesSearch = 
      p.pack_id.toLowerCase().includes(packSearch.toLowerCase()) ||
      p.title.toLowerCase().includes(packSearch.toLowerCase()) ||
      p.summary.toLowerCase().includes(packSearch.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const getDirectivesJson = () => {
    if (!selectedAgent) return '';
    return JSON.stringify({
      "$schema": "https://agentdirective.org/schemas/directive-v1.json",
      "agent_id": selectedAgent.id,
      "name": editName,
      "version": editVersion,
      "target_gateway": "https://agenticscale.org/api/gateway/evaluate",
      "applied_pack": activeAppliedPack ? {
        "pack_id": activeAppliedPack.pack_id,
        "title": activeAppliedPack.title,
        "nist_control_mappings": activeAppliedPack.control_mappings || []
      } : undefined,
      "authority_limits": {
        "max_transaction_limit": editLimit,
        "blast_radius": editBlastRadius,
        "risk_tier": editRiskTier,
        "require_human_dual_control": editLimit > 0
      },
      "allowed_tools": allowedActionsText.split('\n').map(s => s.trim()).filter(Boolean),
      "blocked_tools": restrictedActionsText.split('\n').map(s => s.trim()).filter(Boolean),
      "enforced_guardrails": controlsText.split('\n').map(s => s.trim()).filter(Boolean)
    }, null, 2);
  };

  const getClaudeMd = () => {
    return `# AgenticScale & AgentDirective AI Safety Directives
# Auto-compiled from AgenticScale Safety Profile for: ${editName} (${selectedAgent?.id || 'agent'})
${activeAppliedPack ? `# Applied Control Pack: ${activeAppliedPack.pack_id} (${activeAppliedPack.title})` : ''}

## Zero-Trust Edge Gateway Interception
Before executing any tool that modifies financial ledgers, cloud infrastructure, or customer data, you MUST route the payload through the AgenticScale Gateway:
- **Evaluator URI**: \`https://agenticscale.org/api/gateway/evaluate\`
- **Autonomous Financial Cap**: \$${editLimit.toLocaleString()} USD max per transaction.
- **Dual-Control Gate**: Transactions >\$5,000 or sensitive modifications require human Maker-Checker approval.

## Pre-Approved Tools (ALLOW)
${allowedActionsText.split('\n').map(s => s.trim()).filter(Boolean).map(s => `- \`${s}\``).join('\n') || '- None defined'}

## Strictly Prohibited Tools (BLOCK)
${restrictedActionsText.split('\n').map(s => s.trim()).filter(Boolean).map(s => `- \`${s}\``).join('\n') || '- None defined'}

## Enforced Security Guardrails & Requirements
${controlsText.split('\n').map(s => s.trim()).filter(Boolean).map(s => `- ${s}`).join('\n') || '- Standard Zero-Trust Gateway'}
`;
  };

  const getCursorRules = () => {
    return `// .cursorrules - AgenticScale Safety Directives for ${editName}
You are an AI assistant bound by AgenticScale Edge Gateway safety contracts:
1. ALWAYS verify tool calls against the gateway: https://agenticscale.org/api/gateway/evaluate
2. NEVER execute unapproved mutations in blocked tools: ${restrictedActionsText.split('\n').map(s => s.trim()).filter(Boolean).join(', ')}
3. ENFORCE spend limits of \$${editLimit} USD per transaction.
4. REDACT any customer PII, SSN, or private tokens before logging or tool serialization.
`;
  };

  const getActiveExportContent = () => {
    switch (exporterFormat) {
      case 'directives_json':
        return getDirectivesJson();
      case 'claude_md':
        return getClaudeMd();
      case 'cursorrules':
        return getCursorRules();
    }
  };

  const copyExportContent = async () => {
    try {
      await navigator.clipboard.writeText(getActiveExportContent());
      setCopiedExport(true);
      setTimeout(() => setCopiedExport(false), 2000);
    } catch {
      setCopiedExport(false);
    }
  };

  const downloadExportContent = () => {
    const content = getActiveExportContent();
    const filename = 
      exporterFormat === 'directives_json' ? 'directives.json' :
      exporterFormat === 'claude_md' ? 'CLAUDE.md' : '.cursorrules';
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-8 animate-fade-in max-w-6xl mx-auto">
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">
            Agent Safety Profiles
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1">
            Declarative safety contracts, action whitelists, financial velocity caps, and machine-readable AgentDirectives.
          </p>
        </div>

        <div className="flex items-center space-x-3">
          {!readOnly && (
            <button
              onClick={handleCreateNew}
              className="px-4 py-2 rounded-xl text-xs font-bold bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 transition-all flex items-center space-x-1.5 cursor-pointer"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>New Profile</span>
            </button>
          )}
          <button
            onClick={() => setActiveTab('review')}
            className="px-4 py-2 rounded-xl text-xs font-bold bg-brand-600 hover:bg-brand-500 text-white shadow-sm transition-all cursor-pointer"
          >
            <span>Review from prompt</span>
          </button>
        </div>
      </div>

      {readOnly && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900/60 p-3.5 text-xs text-slate-700 dark:text-slate-300 flex items-center gap-2">
          <Info className="w-4 h-4 text-brand-600 dark:text-brand-400 shrink-0" />
          <span>
            {role === 'viewer'
              ? '👁️ Read-Only Viewer: Safety profile inspection mode. Form editing, spending limit changes, and profile saves are disabled in this view.'
              : 'Read-only workspace: your role can inspect profiles but cannot create, edit, archive, or restore them.'}
          </span>
        </div>
      )}

      {error && (
        <div role="alert" className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs text-rose-800 dark:border-rose-900/50 dark:bg-rose-950/20 dark:text-rose-300">
          {error}
        </div>
      )}

      {/* Main Grid: Left List (4 cols) & Right Form (8 cols) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        
        {/* Left: Fleet Agent Profiles List */}
        <div className="lg:col-span-4 space-y-4">
          <div className="glass-panel p-4 rounded-2xl space-y-2">
            <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-2 px-2">
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Active Profiles</span>
              <span className="text-xs font-semibold text-slate-500">{agents.length} agents</span>
            </div>

            {loading ? (
              <div className="p-8 text-center text-xs text-slate-400">Loading profiles...</div>
            ) : agents.length === 0 ? (
              <div className="p-8 text-center text-xs text-slate-400">No active agents found.</div>
            ) : (
              <div className="space-y-1.5">
                {agents.map((agent) => {
                  const isSelected = selectedAgent?.id === agent.id;
                  return (
                    <button
                      key={agent.id}
                      onClick={() => selectAgentForEdit(agent)}
                      className={`w-full text-left p-3.5 rounded-xl transition-all flex items-center justify-between group cursor-pointer ${
                        isSelected
                          ? 'bg-brand-50/70 dark:bg-slate-800/90 border border-brand-300 dark:border-brand-500/40 shadow-xs'
                          : 'hover:bg-slate-50 dark:hover:bg-slate-800/60 border border-transparent'
                      }`}
                    >
                      <div className="space-y-0.5 overflow-hidden pr-2">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-xs text-slate-900 dark:text-white truncate">
                            {agent.name}
                          </span>
                          <span className={`text-[9px] font-mono px-1.5 py-0.2 rounded-full font-bold uppercase ${
                            agent.status === 'protected' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300' :
                            agent.status === 'monitoring' ? 'bg-blue-100 text-blue-800 dark:bg-blue-950/50 dark:text-blue-300' :
                            agent.status === 'quarantined' ? 'bg-rose-100 text-rose-800 dark:bg-rose-950/50 dark:text-rose-300' :
                            'bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300'
                          }`}>
                            {agent.status}
                          </span>
                        </div>
                        <div className="text-[10px] text-slate-500 truncate">
                          {agent.owner} · Limit: ${agent.max_transaction_limit?.toLocaleString() || '0'}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Right: Active Profile Editor */}
        <div className="lg:col-span-8 min-w-0">
          {selectedAgent ? (
            <div className="glass-panel p-6 sm:p-8 rounded-2xl space-y-6">
              
              {/* Profile Header & Actions */}
              <div className="flex flex-col items-start gap-3 border-b border-slate-200 pb-4 dark:border-slate-800 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white">Edit profile: {editName}</h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{editOwner} · {editVersion}</p>
                </div>

                <div className="flex w-full flex-wrap items-center justify-end gap-2 sm:w-auto">
                  <button
                    type="button"
                    onClick={() => setShowExporterModal(true)}
                    className="px-3.5 py-2 rounded-xl text-xs font-bold bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-200 border border-slate-200 dark:border-slate-700 flex items-center gap-1.5 cursor-pointer shadow-2xs"
                  >
                    <Download className="w-3.5 h-3.5 text-brand-500" />
                    <span>Export Directives</span>
                  </button>

                  {!readOnly && agents.some((agent) => agent.id === selectedAgent.id) && (
                    <button
                      type="button"
                      onClick={handleArchive}
                      disabled={saving}
                      className="px-3 py-2 rounded-xl text-xs font-semibold border border-rose-200 text-rose-700 hover:bg-rose-50 dark:border-rose-900/50 dark:text-rose-300 dark:hover:bg-rose-950/30 flex items-center gap-1.5 cursor-pointer"
                    >
                      <Archive className="w-3.5 h-3.5" /> Archive
                    </button>
                  )}

                  {!readOnly && (
                    <button
                      type="button"
                      onClick={handleSave}
                      disabled={saving}
                      className="px-5 py-2 rounded-xl text-xs font-bold bg-emerald-600 hover:bg-emerald-500 text-white shadow-sm flex items-center space-x-1.5 transition-all cursor-pointer"
                    >
                      {savedSuccess ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
                      <span>{saving ? 'Saving...' : savedSuccess ? 'Saved!' : 'Save Changes'}</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Complete 29 Control Pack Library (AgentDirective Ported) */}
              <div className="p-4 sm:p-5 rounded-3xl bg-brand-50/60 dark:bg-brand-950/20 border border-brand-200/80 dark:border-brand-500/20 space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <span className="text-xs font-bold text-brand-900 dark:text-brand-300 flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5 text-brand-600 dark:text-brand-400" />
                    <span>Evidence-Grounded Control Pack Library (29 Packs · AgentDirective)</span>
                  </span>
                  <span className="text-[10px] text-brand-600 dark:text-brand-400 font-mono font-bold">1-Click Apply to Form</span>
                </div>

                {/* Category Pills & Search */}
                <div className="flex flex-wrap items-center gap-1.5">
                  {[
                    { id: 'ALL', label: 'All Packs (29)' },
                    { id: 'AI', label: 'AI Safety (AI)' },
                    { id: 'AUT', label: 'Autonomous Authority (AUT)' },
                    { id: 'DAT', label: 'Data & DLP (DAT)' },
                    { id: 'IDN', label: 'Identity & IAM (IDN)' },
                    { id: 'OBS', label: 'Observability (OBS)' },
                    { id: 'REL', label: 'Reliability (REL)' }
                  ].map((cat) => (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => setPackCategoryFilter(cat.id)}
                      className={`px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase transition cursor-pointer ${
                        packCategoryFilter === cat.id
                          ? 'bg-brand-600 text-white shadow-2xs'
                          : 'bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                      }`}
                    >
                      {cat.label}
                    </button>
                  ))}
                </div>

                {/* Pack Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 max-h-56 overflow-y-auto pr-1">
                  {filteredPacks.map((pack) => (
                    <button
                      key={pack.pack_id}
                      type="button"
                      onClick={() => applyControlPack(pack)}
                      disabled={readOnly}
                      className={`text-left p-2.5 rounded-xl border transition-all text-xs space-y-1 shadow-2xs cursor-pointer ${
                        activeAppliedPack?.pack_id === pack.pack_id
                          ? 'bg-brand-100 dark:bg-brand-900/60 border-brand-500 text-brand-900 dark:text-white'
                          : 'bg-white dark:bg-slate-900 border-brand-200 dark:border-brand-800/60 hover:border-brand-500'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-mono font-bold px-1.5 py-0.2 rounded bg-brand-100 dark:bg-brand-500/20 text-brand-800 dark:text-brand-300">
                          {pack.pack_id}
                        </span>
                        <span className="text-[9px] font-bold text-slate-400">v{pack.version}</span>
                      </div>
                      <div className="font-bold text-slate-900 dark:text-white text-[11px] leading-snug line-clamp-1">
                        {pack.title}
                      </div>
                      <div className="text-[10px] text-slate-500 line-clamp-1">
                        {pack.summary}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Form Fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="profile-agent-name" className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-1">Agent Name</label>
                  <input
                    id="profile-agent-name"
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    disabled={readOnly}
                    className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl px-3 py-2 text-xs text-slate-900 dark:text-white focus:outline-none focus:border-brand-500 shadow-sm"
                  />
                </div>
                <div>
                  <label htmlFor="profile-owner" className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-1">Owning Team</label>
                  <input
                    id="profile-owner"
                    type="text"
                    value={editOwner}
                    onChange={(e) => setEditOwner(e.target.value)}
                    disabled={readOnly}
                    className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl px-3 py-2 text-xs text-slate-900 dark:text-white focus:outline-none focus:border-brand-500 shadow-sm"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="profile-purpose" className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-1">Agent Purpose & Scope</label>
                <textarea
                  id="profile-purpose"
                  rows={2}
                  value={editPurpose}
                  onChange={(e) => setEditPurpose(e.target.value)}
                  disabled={readOnly}
                  className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl p-3 text-xs text-slate-900 dark:text-white focus:outline-none focus:border-brand-500 leading-relaxed shadow-sm"
                />
              </div>

              <div>
                <label htmlFor="profile-version" className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-1">Version</label>
                <input id="profile-version" value={editVersion} onChange={(e) => setEditVersion(e.target.value)} disabled={readOnly} placeholder="v1.0.0" className="w-full sm:w-1/2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl px-3 py-2 text-xs text-slate-900 dark:text-white focus:outline-none focus:border-brand-500 disabled:cursor-not-allowed disabled:opacity-60" />
              </div>

              {/* Governance Controls */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                <div>
                  <label htmlFor="profile-status" className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-1">Governance Status</label>
                  <select
                    id="profile-status"
                    value={editStatus}
                    onChange={(e: any) => setEditStatus(e.target.value)}
                    disabled={readOnly}
                    className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl px-3 py-2 text-xs text-slate-900 dark:text-white focus:outline-none focus:border-brand-500 shadow-sm"
                  >
                    <option value="protected">Protected</option>
                    <option value="monitoring">Monitoring</option>
                    <option value="at_risk">At-Risk</option>
                    <option value="quarantined">Quarantined</option>
                  </select>
                  {selectedAgent.status === 'quarantined' && <p className="mt-2 text-[10px] leading-4 text-amber-700 dark:text-amber-300">This agent is stopped. After investigation, choose Monitoring or Protected and save to release it; the change will create a new history entry and audit record.</p>}
                </div>

                <div>
                  <label htmlFor="profile-blast-radius" className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-1">Blast Radius</label>
                  <select
                    id="profile-blast-radius"
                    value={editBlastRadius}
                    onChange={(e: any) => setEditBlastRadius(e.target.value)}
                    disabled={readOnly}
                    className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl px-3 py-2 text-xs text-slate-900 dark:text-white focus:outline-none focus:border-brand-500 shadow-sm"
                  >
                    <option value="low">Low Impact</option>
                    <option value="medium">Medium Impact</option>
                    <option value="high">High Impact</option>
                    <option value="critical">Critical Impact</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="profile-limit" className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-1">Autonomous Limit ($USD)</label>
                  <div className="relative">
                    <DollarSign className="w-3.5 h-3.5 absolute left-3 top-2.5 text-slate-400" />
                    <input
                      id="profile-limit"
                      type="number"
                      value={editLimit}
                      onChange={(e) => setEditLimit(Number(e.target.value))}
                      disabled={readOnly}
                      className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl pl-8 pr-3 py-2 text-xs text-slate-900 dark:text-white focus:outline-none focus:border-brand-500 font-mono shadow-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Whitelist & Blacklist Boundaries */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                <div>
                  <label htmlFor="profile-allowed-actions" className="text-xs font-bold text-emerald-700 dark:text-emerald-400 block mb-1 flex items-center space-x-1">
                    <span>Allowed actions</span>
                  </label>
                  <textarea
                    id="profile-allowed-actions"
                    rows={4}
                    value={allowedActionsText}
                    onChange={(e) => setAllowedActionsText(e.target.value)}
                    disabled={readOnly}
                    className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl p-3 text-xs text-slate-900 dark:text-white font-mono focus:outline-none focus:border-emerald-500 leading-relaxed shadow-sm"
                    placeholder="read_invoice&#10;extract_metadata"
                  />
                </div>

                <div>
                  <label htmlFor="profile-restricted-actions" className="text-xs font-bold text-rose-700 dark:text-rose-400 block mb-1 flex items-center space-x-1">
                    <span>Blocked actions</span>
                  </label>
                  <textarea
                    id="profile-restricted-actions"
                    rows={4}
                    value={restrictedActionsText}
                    onChange={(e) => setRestrictedActionsText(e.target.value)}
                    disabled={readOnly}
                    className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl p-3 text-xs text-slate-900 dark:text-white font-mono focus:outline-none focus:border-rose-500 leading-relaxed shadow-sm"
                    placeholder="disable_audit_logging&#10;delete_audit_records"
                  />
                </div>
              </div>

              {/* Required Controls */}
              <div>
                <label htmlFor="profile-controls" className="text-xs font-bold text-brand-700 dark:text-brand-300 block mb-1">
                  Required safeguards
                </label>
                <textarea
                  id="profile-controls"
                  rows={3}
                  value={controlsText}
                  onChange={(e) => setControlsText(e.target.value)}
                  disabled={readOnly}
                  className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl p-3 text-xs text-slate-900 dark:text-white font-mono focus:outline-none focus:border-brand-500 shadow-sm"
                  placeholder="human_approval_over_5k&#10;vendor_bank_change_dual_control"
                />
              </div>

              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label htmlFor="profile-change-reason" className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-1">Change reason</label>
                  <input id="profile-change-reason" value={changeReason} onChange={(e) => setChangeReason(e.target.value)} disabled={readOnly} placeholder="Why is this boundary changing?" className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl px-3 py-2 text-xs text-slate-900 dark:text-white focus:outline-none focus:border-brand-500 disabled:cursor-not-allowed disabled:opacity-60" />
                </div>
              </div>

              <details className="border-t border-slate-200 dark:border-slate-800 pt-4">
                <summary className="cursor-pointer list-none text-sm font-bold text-slate-900 dark:text-white">View profile history</summary>
                {historyLoading ? (
                  <p role="status" className="text-xs text-slate-500 mt-2">Loading profile history…</p>
                ) : historyError ? (
                  <div role="alert" className="mt-2 space-y-2 text-xs text-rose-700 dark:text-rose-300">
                    <p>Profile history couldn’t load. {historyError}</p>
                    <button type="button" onClick={() => setHistoryRevision((revision) => revision + 1)}
                      className="rounded-lg border border-rose-200 px-3 py-2 font-bold dark:border-rose-800">
                      Retry profile history
                    </button>
                  </div>
                ) : profileHistory.length === 0 ? (
                  <p className="text-xs text-slate-500 mt-2">{hasStoredProfile ? 'No version history is available yet.' : 'Save this profile to start its version history.'}</p>
                ) : (
                  <div className="mt-2 space-y-2">{profileHistory.slice(0, 5).map((entry) => (
                    <div key={entry.id} className="rounded-lg bg-slate-50 dark:bg-slate-950/50 border border-slate-200 dark:border-slate-800 px-3 py-2 text-[11px]">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div><span className="font-mono font-semibold">{entry.version}</span><span className="ml-2 text-slate-500">{new Date(entry.created_at).toLocaleString()}</span></div>
                        {!readOnly && <button type="button" onClick={() => handleRollback(entry)} disabled={saving} className="inline-flex items-center gap-1 rounded-lg border border-brand-200 bg-white px-2 py-1 text-[10px] font-bold text-brand-700 hover:bg-brand-50 disabled:opacity-50 dark:border-brand-500/30 dark:bg-slate-900 dark:text-brand-300"><Undo2 className="h-3 w-3" />Use this version</button>}
                      </div>
                      <div className="text-slate-600 dark:text-slate-400 mt-1">{entry.change_reason || 'Profile change recorded.'}</div>
                    </div>
                  ))}</div>
                )}
              </details>

              <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between">
                <span className="text-xs text-slate-500 dark:text-slate-400">{readOnly ? 'Review profile boundaries and version history before validation.' : 'Save edits before running validation. Unsaved changes are not included.'}</span>
                <button
                  type="button"
                  onClick={() => setActiveTab('validate')}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-brand-600 hover:bg-brand-500 text-white font-bold text-xs shadow-sm transition cursor-pointer"
                >
                  <span>Stage 3: Run Pre-Release Validation</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>

            </div>
          ) : (
            <div className="glass-panel p-12 text-center text-slate-500 text-xs">
              Select or create an agent profile to begin editing.
            </div>
          )}
        </div>

      </div>

      {/* AgentDirective 1-Click Exporter Modal */}
      {showExporterModal && selectedAgent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm animate-fade-in">
          <div className="relative w-full max-w-2xl rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6 sm:p-8 space-y-5 shadow-2xl">
            
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                    Export AgentDirectives & Project Rules
                  </h3>
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-brand-100 dark:bg-brand-500/20 text-brand-700 dark:text-brand-300 font-bold">
                    AgentDirective Grounded
                  </span>
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Export machine-readable directives and prompt files for coding agents (Claude Code, Cursor, Copilot, Antigravity).
                </p>
              </div>

              <button
                type="button"
                aria-label="Close directives export"
                onClick={() => setShowExporterModal(false)}
                className="p-1.5 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Format Tabs */}
            <div className="flex items-center gap-2 p-1 rounded-xl bg-slate-100 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-xs font-bold">
              {[
                { id: 'directives_json', label: '.agentdirective/directives.json' },
                { id: 'claude_md', label: 'CLAUDE.md' },
                { id: 'cursorrules', label: '.cursorrules' }
              ].map((f) => {
                const isCur = exporterFormat === f.id;
                return (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => setExporterFormat(f.id as any)}
                    className={`flex-1 py-1.5 rounded-lg transition cursor-pointer text-center ${
                      isCur
                        ? 'bg-white dark:bg-slate-800 text-brand-600 dark:text-brand-400 shadow-xs'
                        : 'text-slate-500 hover:text-slate-800 dark:hover:text-white'
                    }`}
                  >
                    {f.label}
                  </button>
                );
              })}
            </div>

            {/* Code Box */}
            <div className="relative rounded-2xl bg-slate-950 p-4 font-mono text-xs text-slate-100 border border-slate-800 max-h-72 overflow-y-auto">
              <pre className="text-[11.5px] leading-relaxed"><code>{getActiveExportContent()}</code></pre>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-800">
              <span className="text-[11px] text-slate-500 dark:text-slate-400">
                Drop this file in your project root to auto-enforce edge gateway intercept.
              </span>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={copyExportContent}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-200 transition cursor-pointer"
                >
                  {copiedExport ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                  <span>{copiedExport ? 'Copied!' : 'Copy Code'}</span>
                </button>

                <button
                  type="button"
                  onClick={downloadExportContent}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold bg-brand-600 hover:bg-brand-500 text-white shadow-sm transition cursor-pointer"
                >
                  <Download className="w-3.5 h-3.5" />
                  <span>Download File</span>
                </button>
              </div>
            </div>

          </div>
        </div>
      )}

    </div>
  );
};
